<template>
    <div id="order-audit-queue-container">
        <card title="Order Audits">
            <div>
                <b-row>
                    <b-col>
                        <SearchComponent v-model="storeFilter" label="Store" search-u-r-l="/Store" :params="{active: true}" clearable prepopulate
                            @input="search" />
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="programFilter" label="Program" search-u-r-l="/Program"
                            :params="{ active: true }" clearable prepopulate @input="search" />
                    </b-col>
                </b-row>
                <b-button variant="success" @click="processQueue"
                    :disabled="!(orderAudits && orderAudits.length) || disableProcessButton">
                    Process Queue...
                </b-button>
            </div>

            <b-table id="order-audit-table" ref="order-audit-table" outlined sort-icon-left show-empty
                empty-text="No Orders Requiring Audit" empty-filtered-text="No Orders Requiring Audit"
                :fields="tableFields" :items="pagedOrderAudits" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
                :busy="loading" @sort-changed="handleSort">
                <template #table-busy>
                    <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle" />
                        <strong>Loading...</strong>
                    </div>
                </template>

                <template #cell(edit)="data">
                    <router-link :to="{ name: 'OrderAuditDetails', params: { id: data.item.orderId } }">
                        <b-icon icon="clipboard-data" />
                    </router-link>
                </template>

                <template #cell(promisedDate)="data">
                    <PromisedTimeSummary v-if="data.value" :value="data.value" size="sm" />
                </template>
            </b-table>

            <BottomPagerBar v-model="pageNumber" :total-rows="orderAudits.length" :per-page="pageSize" show-total
                item-name="order audit" :show-page-size-selector="true" :loading="loading"
                @change-page-size="pageSize = $event" />

            <!-- Order Audit Modal -->
            <b-modal id="order-audit-modal" size="xl" title="Process Queue" hide-footer no-close-on-backdrop>
                <OrderAuditDetails v-if="currentAuditOrder" :order-id="currentAuditOrder.orderId" :queue-mode="true"
                    @completed="onAuditCompleted" />

                <hr>

                <div class="d-flex justify-content-between">
                    <div>
                        <b-button variant="secondary" @click="previousAudit" :disabled="currentAuditIndex <= 0">
                            <i class="fa fa-chevron-left"></i> Previous
                        </b-button>
                        <b-button variant="secondary" @click="nextAudit"
                            :disabled="currentAuditIndex >= queueForProcessing.length - 1">
                            Next <i class="fa fa-chevron-right"></i>
                        </b-button>
                    </div>
                    <div>
                        <span class="mr-3">{{ currentAuditIndex + 1 }} of {{ queueForProcessing.length }}</span>
                        <b-button variant="dark" @click="closeQueue">
                            Close Queue
                        </b-button>
                    </div>
                </div>
            </b-modal>

        </card>
    </div>
</template>

<script lang="ts">

import { Component, Vue, Watch } from "vue-property-decorator";
import SearchComponent from "@/components/SearchComponent.vue";
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import Axios from "axios";
import { OrderAuditRow } from "@/models/OrderAudit";
import moment from "moment";
import PromisedTimeSummary from "@/components/Order/PromisedTimeSummaryComponent.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import OrderAuditDetails from "./OrderAuditDetails.vue";
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';

@Component({
    name: "OrderAuditQueuePage",
    components: {
        SearchComponent,
        PromisedTimeSummary,
        BottomPagerBar,
        OrderAuditDetails
    },
})
export default class OrderAuditQueuePage extends Vue {

    private orderAudits: Array<OrderAuditRow> = [];
    private storeFilter: Store = new Store();
    private programFilter: Program = new Program();

    protected pageNumber: number = 1;
    protected pageSize: number = 100;
    protected loading: boolean = true;

    private sortBy: string = "orderDate";
    private sortDesc: boolean = true;

    // Queue processing state
    private queueForProcessing: Array<OrderAuditRow> = [];
    private currentAuditIndex: number = 0;

    @Watch("storeFilter")
    @Watch("programFilter")
    resetPage() {
        this.pageNumber = 1;
    }

    private currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

    get tableFields() {
        return [
            { key: "orderId", sortable: true },
            { key: "orderDate", sortable: true, formatter: (d: any) => moment.utc(d).local().format("L LT"), },
            {
                key: "orderCreatedBy",
                sortable: true,
                formatter(d: any) {
                    if (d.includes("/")) return d.split("/")[1];
                    if (d.includes("\\")) return d.split("\\")[1];
                    return d.split("@")[0];
                }
            },
            { key: "promisedDate", sortable: true, hidden: !this.hasPromised },
            { key: "patientName", sortable: true, },
            { key: "drugName", sortable: true, },
            { key: "copay", sortable: true, formatter: this.currencyFormatter.format, },
            { key: "programName", sortable: true, },
            { key: "auditReason", sortable: true, },
            { key: "edit", },
        ].filter(f => f.hidden !== true);
    }

    private hasPromised = false;

    created() {
        this.pageSize = getPageSizePreference(this, 'pageSize_OrderAudit', 100);
        this.search();
    }

    @Watch("pageSize")
    onPageSizeChanged() {
        savePageSizePreference(this, 'pageSize_OrderAudit', this.pageSize);
    }

    async search() {
        try {
            this.loading = true;
            const response = await Axios.get('/Order/Audit/Queue', {
                params: {
                    includeCompleted: false,
                    storeId: this.storeFilter?.id ?? 0,
                    programId: this.programFilter?.id ?? 0
                }
            });

            this.orderAudits = response.data.map((a: any) => new OrderAuditRow(a));
            this.hasPromised = !!this.orderAudits.find(i => i.promisedDate);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.warn("Error while fetching order audit queue", error);
        } finally {
            this.loading = false;
        }
    }

    get disableProcessButton(): boolean {
        return !this.orderAudits.length;
    }

    get sortedOrderAudits() {
        let sorted = [...this.orderAudits];

        switch (this.sortBy) {
            case "orderId":
                sorted.sort((a, b) => a.orderId - b.orderId);
                break;
            case "orderDate":
                sorted.sort((a, b) => new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime());
                break;
            case "orderCreatedBy":
                sorted.sort((a, b) => a.orderCreatedBy?.localeCompare(b.orderCreatedBy));
                break;
            case "patientName":
                sorted.sort((a, b) => a.patientName?.localeCompare(b.patientName));
                break;
            case "drugName":
                sorted.sort((a, b) => a.drugName?.localeCompare(b.drugName));
                break;
            case "copay":
                sorted.sort((a, b) => a.copay - b.copay);
                break;
            case "programName":
                sorted.sort((a, b) => a.programName?.localeCompare(b.programName));
                break;
            case "auditReason":
                sorted.sort((a, b) => a.auditReason?.localeCompare(b.auditReason));
                break;
            default:
                sorted.sort((a, b) => new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime());
                break;
        }

        if (this.sortDesc) sorted.reverse();

        return sorted;
    }

    get pagedOrderAudits() {
        const startIndex = this.pageSize * this.pageNumber - this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return this.sortedOrderAudits.slice(startIndex, endIndex);
    }

    get currentAuditOrder() {
        return this.queueForProcessing[this.currentAuditIndex];
    }

    handleSort(event: any) {
        this.sortBy = event.sortBy;
        this.sortDesc = event.sortDesc;
        this.pageNumber = 1;
    }

    processQueue() {
        // Only process incomplete audits
        this.queueForProcessing = this.sortedOrderAudits.filter(audit => !audit.auditId);
        this.currentAuditIndex = 0;
        this.$bvModal.show("order-audit-modal");
    }

    nextAudit() {
        if (this.currentAuditIndex < this.queueForProcessing.length - 1) {
            this.currentAuditIndex++;
        }
    }

    previousAudit() {
        if (this.currentAuditIndex > 0) {
            this.currentAuditIndex--;
        }
    }

    onAuditCompleted() {
        // Remove completed audit from the queue
        this.queueForProcessing.splice(this.currentAuditIndex, 1);

        // Adjust index if we're at the end
        if (this.currentAuditIndex >= this.queueForProcessing.length && this.queueForProcessing.length > 0) {
            this.currentAuditIndex = this.queueForProcessing.length - 1;
        }

        // If no more audits to process, close modal and refresh
        if (this.queueForProcessing.length === 0) {
            this.closeQueue();
        }
    }

    closeQueue() {
        this.$bvModal.hide("order-audit-modal");
        this.queueForProcessing = [];
        this.currentAuditIndex = 0;
        this.search(); // Refresh the main list
    }
}
</script>

<style scoped></style>
